<script setup>
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useDraggableResizable } from 'dashboard/composables/useDraggableResizable';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value => ['3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const dialogContentRef = ref(null);
const isOpen = ref(false);

const { dragStyle, isDragging, startDrag, startResize, reset } = useDraggableResizable();

// Keep only transform/transition on the <dialog> element (for drag positioning).
// Width/height go on the <form> so there's no empty space inside the dialog wrapper.
const dialogMoveStyle = computed(() => ({
  transform: dragStyle.value.transform,
  transition: dragStyle.value.transition,
  userSelect: dragStyle.value.userSelect,
}));

const formSizeStyle = computed(() => {
  const s = {};
  if (dragStyle.value.width)  { s.width = dragStyle.value.width; s.maxWidth = 'none'; }
  if (dragStyle.value.height) { s.height = dragStyle.value.height; s.maxHeight = 'none'; s.overflowY = 'auto'; }
  return s;
});

const maxWidthClass = computed(() => {
  const classesMap = {
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

const open = () => {
  reset();
  isOpen.value = true;
  dialogRef.value?.showModal();
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
  isOpen.value = false;
  reset();
};

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => e.target === dialogRef.value && close();

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] === dialogRef.value) close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full shadow-xl rounded-xl overflow-visible bg-transparent"
      :class="[
        maxWidthClass,
        positionClass,
        isDragging ? 'transition-none' : 'transition-all duration-300 ease-in-out',
      ]"
      :style="dialogMoveStyle"
      @close.prevent="handleDialogClose"
    >
      <OnClickOutside @trigger="handleClickOutside">
        <form
          ref="dialogContentRef"
          class="relative flex flex-col w-full h-auto max-h-[90vh] gap-6 p-6 overflow-hidden text-start align-middle transition-all duration-300 ease-in-out transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-xl"
          :style="formSizeStyle"
          @submit.prevent="confirm"
          @click.stop
        >
          <!-- Drag handle bar -->
          <div
            class="absolute top-0 left-0 right-0 h-8 rounded-t-xl flex items-center justify-center cursor-grab active:cursor-grabbing z-10 select-none"
            @mousedown="startDrag"
          >
            <div class="flex gap-0.5">
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
              <span class="w-1 h-1 rounded-full bg-n-slate-7 opacity-50" />
            </div>
          </div>

          <div v-if="title || description" class="flex flex-col gap-2 mt-2">
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <div v-if="isOpen" class="flex-1 min-h-0 overflow-y-auto">
            <slot />
          </div>
          <!-- Dialog content will be injected here -->
          <slot name="footer">
            <div
              v-if="showCancelButton || showConfirmButton"
              class="flex items-center justify-between w-full gap-3"
            >
              <Button
                v-if="showCancelButton"
                variant="faded"
                color="slate"
                :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                class="w-full"
                type="button"
                @click="close"
              />
              <Button
                v-if="showConfirmButton"
                :color="type === 'edit' ? 'blue' : 'ruby'"
                :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                class="w-full"
                :is-loading="isLoading"
                :disabled="disableConfirmButton || isLoading"
                type="submit"
              />
            </div>
          </slot>

          <!-- Resize handle -->
          <div
            class="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize flex items-end justify-end pb-1 pr-1 opacity-30 hover:opacity-80 transition-opacity"
            @mousedown="e => startResize(e, dialogContentRef)"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" class="text-n-slate-9">
              <circle cx="8" cy="8" r="1.2"/>
              <circle cx="4.5" cy="8" r="1.2"/>
              <circle cx="8" cy="4.5" r="1.2"/>
            </svg>
          </div>
        </form>
      </OnClickOutside>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

.dialog-position-top {
  margin-top: clamp(2rem, 5vh, 5rem);
  margin-bottom: auto;
}
</style>
